"use client";

import { Navbar1 } from "@/components/ui/Standards/navbar";
import { Footer7 } from "@/components/ui/Standards/footer-7";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import Link from "next/link";
import {
  CheckCircle2,
  XCircle,
  Wind,
  Thermometer,
  Filter,
  Volume2,
} from "lucide-react";

export default function VadArEnrumsventilationPage() {
  return (
    <>
      <Navbar1 />
      <main className="min-h-screen bg-background">
        {/* Hero Section */}
        <section className="container px-4 py-16 mx-auto lg:py-24">
          <div className="max-w-4xl mx-auto">
            <Badge className="mb-4">Guide</Badge>
            <h1 className="mb-6 text-5xl font-bold tracking-tight md:text-6xl">
              Vad är enrumsventilation?
            </h1>
            <p className="text-xl leading-relaxed text-muted-foreground">
              Ventilation för ett enskilt rum – lösningen när du vill förbättra
              luftkvaliteten i ett specifikt rum utan att bygga om hela huset.
            </p>
          </div>
        </section>

        {/* Introduction */}
        <section className="py-16 bg-muted/30">
          <div className="container max-w-4xl px-4 mx-auto">
            <div className="prose prose-lg max-w-none">
              <p className="text-lg leading-relaxed">
                Många svenska villor har ett eller flera rum där ventilationen
                inte räcker till. Det kan vara ett badrum som aldrig riktigt
                torkar, ett sovrum med tung luft, en källare som luktar instängt
                eller en tvättstuga där fukten stannar kvar.
              </p>
              <p className="mt-4 text-lg leading-relaxed">
                <strong>Enrumsventilation</strong> är lösningen när du vill
                förbättra luftkvaliteten i ett specifikt rum utan att bygga om
                hela huset eller installera ett stort centralt
                ventilationssystem.
              </p>
            </div>
          </div>
        </section>

        {/* When Do You Need It */}
        <section className="py-16">
          <div className="container max-w-4xl px-4 mx-auto">
            <h2 className="mb-8 text-3xl font-bold">
              När behöver du enrumsventilation?
            </h2>
            <div className="grid gap-6 md:grid-cols-2">
              {[
                "Ett badrum som inte torkar ordentligt",
                "Ett sovrum med tung eller stillastående luft",
                "En källare med lukt eller fukt",
                "En tvättstuga där kläderna torkar långsamt",
                "En uthyrningsdel som behöver egen ventilation",
                "Ett kontor eller arbetsrum med dålig luftkvalitet",
              ].map((item, index) => (
                <div
                  key={index}
                  className="flex items-start gap-3 p-4 bg-white rounded-lg shadow"
                >
                  <CheckCircle2 className="flex-shrink-0 w-6 h-6 mt-1 text-primary" />
                  <p className="text-base">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Three Types */}
        <section className="py-16 bg-muted/30">
          <div className="container max-w-4xl px-4 mx-auto">
            <h2 className="mb-12 text-3xl font-bold text-center">
              Tre huvudtyper av enrumsventilation
            </h2>

            <div className="space-y-8">
              {/* Type 1: Frånluftsfläkt */}
              <div className="p-8 bg-white shadow-lg rounded-xl">
                <h3 className="mb-4 text-2xl font-semibold">
                  1. Frånluftsfläkt – enkel men okontrollerad
                </h3>
                <p className="mb-4 text-muted-foreground">
                  En frånluftsfläkt suger ut luft och skapar undertryck.
                  Tilluften kommer in via otätheter eller ventiler.
                </p>
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <p className="mb-2 font-semibold text-green-600">
                      Fördelar:
                    </p>
                    <ul className="space-y-1 text-sm">
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5" />
                        <span>Billigt</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5" />
                        <span>Enkelt att installera</span>
                      </li>
                    </ul>
                  </div>
                  <div>
                    <p className="mb-2 font-semibold text-red-600">
                      Nackdelar:
                    </p>
                    <ul className="space-y-1 text-sm">
                      <li className="flex items-start gap-2">
                        <XCircle className="w-4 h-4 text-red-600 mt-0.5" />
                        <span>Ingen värmeåtervinning</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <XCircle className="w-4 h-4 text-red-600 mt-0.5" />
                        <span>Okontrollerad tilluft</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <XCircle className="w-4 h-4 text-red-600 mt-0.5" />
                        <span>Risk för kallras</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Type 2: Tilluftsventil */}
              <div className="p-8 bg-white shadow-lg rounded-xl">
                <h3 className="mb-4 text-2xl font-semibold">
                  2. Tilluftsventil – passiv lösning
                </h3>
                <p className="mb-4 text-muted-foreground">
                  En tilluftsventil släpper in luft, men kräver att frånluften
                  fungerar bra.
                </p>
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <p className="mb-2 font-semibold text-green-600">
                      Fördelar:
                    </p>
                    <ul className="space-y-1 text-sm">
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5" />
                        <span>Billigt</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5" />
                        <span>Enkelt</span>
                      </li>
                    </ul>
                  </div>
                  <div>
                    <p className="mb-2 font-semibold text-red-600">
                      Nackdelar:
                    </p>
                    <ul className="space-y-1 text-sm">
                      <li className="flex items-start gap-2">
                        <XCircle className="w-4 h-4 text-red-600 mt-0.5" />
                        <span>Ingen värmeåtervinning</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <XCircle className="w-4 h-4 text-red-600 mt-0.5" />
                        <span>Ingen aktiv frånluft</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <XCircle className="w-4 h-4 text-red-600 mt-0.5" />
                        <span>Påverkas av väder och vind</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Type 3: Mini-FTX */}
              <div className="p-8 border-2 shadow-lg bg-primary/5 border-primary rounded-xl">
                <div className="flex items-center gap-2 mb-4">
                  <h3 className="text-2xl font-semibold">
                    3. Mini-FTX – modern och energieffektiv
                  </h3>
                  <Badge variant="default">Rekommenderas</Badge>
                </div>
                <p className="mb-4 text-muted-foreground">
                  Mini-FTX är den mest avancerade formen av enrumsventilation.
                </p>
                <div className="grid gap-3 mb-6 md:grid-cols-2 lg:grid-cols-4">
                  <div className="flex items-center gap-2 p-3 bg-white rounded-lg">
                    <Wind className="w-5 h-5 text-primary" />
                    <span className="text-sm font-medium">
                      Tilluft & Frånluft
                    </span>
                  </div>
                  <div className="flex items-center gap-2 p-3 bg-white rounded-lg">
                    <Thermometer className="w-5 h-5 text-primary" />
                    <span className="text-sm font-medium">
                      Värmeåtervinning
                    </span>
                  </div>
                  <div className="flex items-center gap-2 p-3 bg-white rounded-lg">
                    <Filter className="w-5 h-5 text-primary" />
                    <span className="text-sm font-medium">Filtrering</span>
                  </div>
                  <div className="flex items-center gap-2 p-3 bg-white rounded-lg">
                    <Volume2 className="w-5 h-5 text-primary" />
                    <span className="text-sm font-medium">Tyst drift</span>
                  </div>
                </div>
                <p className="mb-4 text-sm text-muted-foreground">
                  Mini-FTX är idag den tryggaste och mest energieffektiva
                  lösningen för ett enskilt rum.
                </p>
                <Button asChild size="lg" className="rounded-full">
                  <Link href="https://miniftx.se">Läs mer om Mini-FTX</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16">
          <div className="container max-w-4xl px-4 mx-auto text-center">
            <h2 className="mb-4 text-3xl font-bold">
              Vill du veta mer om enrumsventilation?
            </h2>
            <p className="mb-8 text-lg text-muted-foreground">
              Kontakta våra samarbetspartners för kostnadsfri rådgivning och
              offert
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button asChild size="lg" className="rounded-full">
                <Link href="/vad-ar-enrumsventilation/problem-losningar">
                  Se problem & lösningar
                </Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="rounded-full"
              >
                <Link href="/ventilation-installation">Kontakta oss</Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
      <Footer7 />
    </>
  );
}
